import React, { useState } from "react";
import { withRouter } from "react-router-dom";
import styled from "styled-components";
import {
    BusinessPortalHeader,
    Button,
    LottieLoadingIndicator,
    PortalCaption,
    PortalKicker,
    PortalPageShell,
    PortalSectionTitle,
    PortalSurface,
} from "src/components";
import useGetDigitalMemberCard from "./hooks/useGetDigitalMemberCard";
import useGetHeroImages from "src/scenes/DigitalMemberCard/hooks/useGetHeroImages";
import DigitalCompanyCardContext from "./DigitalCompanyCardContext";
import DisplayCompanyCards from "./components/DisplayCompanyCards";
import CustomiseUserTypeCard from "./components/CustomiseUserTypeCard";
import { DIGITAL_MEMBER_CARD_PAGES } from "./utils/enum";
import pluralize from "pluralize";
import { customNames } from "src/utils/helpers";
import PencilIcon from "src/img/edit@2x.png";
import ChangeMembershipNameDialog from "./components/ChangeMembershipNameDialog";

export const getMembershipCardHeader = (user) => {
    return user?.customMembershipCardName || "Digital Member Card";
};

const DisclaimerPanel = styled(PortalSurface)`
    margin-bottom: 1rem;
    box-shadow: none;
    background: #F3F5FB;
`;

const DigitalWorkspaceSurface = styled(PortalSurface)`
    width: 100%;
    max-width: ${p => p.$isCustomise ? "70rem" : "none"};
    min-height: 44.188rem;
    display: grid;
    align-items: center;
    overflow-x: auto;
    padding: 0;
`;

const DigitalMemberCard = ({ user }) => {
    const [renderPage, setRenderPage] = useState(DIGITAL_MEMBER_CARD_PAGES.DISPLAY_COMPANY_CARD);
    const { digitalMembershipCards, getCardTemplateByUserType, isFetchingDigitalMemberCard, employeeCardInfo, clientCardInfo, fetchDigitalMemberCard } = useGetDigitalMemberCard(user.token);
    const [showChangeNameDialog, setShowChangeNameDialog] = useState(false);
    const heading = getMembershipCardHeader(user);
    const { 
        businessHeroImages, 
        setBusinessHeroImages,
        meHeroImages,
        isFetchingHeroImages,
        fetchHeroImages
    } = useGetHeroImages(user.token);
    
    const PageHeader = () => (
        <>
            <BusinessPortalHeader
                title={heading}
                description={`Create unique ${heading.toLowerCase()} for your ${pluralize.singular(customNames.employees.toLowerCase())} and ${pluralize.singular(customNames.clients.toLowerCase())}, including company logo, background colour, and personal details for digital wallet access.`}
            >
                <Button
                    backgroundColor="#171A22"
                    color="#FFFFFF"
                    height="42px"
                    borderRadius="8px"
                    padding="0 1rem"
                    gap="0.5rem"
                    onClick={() => setShowChangeNameDialog(true)}
                >
                    Change Name
                    <img
                        src={PencilIcon}
                        height="13px"
                        width="13px"
                        alt=""
                    />
                </Button>
            </BusinessPortalHeader>
            <DisclaimerPanel padding="1rem">
                <PortalKicker>Preview Context</PortalKicker>
                <PortalSectionTitle>{heading} Preview Disclaimer</PortalSectionTitle>
                <PortalCaption maxWidth="62rem">
                    The information displayed on {pluralize(customNames.employees.toLowerCase())} and {pluralize.singular(customNames.clients.toLowerCase())} card previews is for example purposes only. You can test adding your contact using the QR code on either card preview.
                </PortalCaption>
            </DisclaimerPanel>
            {showChangeNameDialog && (
                <ChangeMembershipNameDialog
                    hideShowNameChangeDialog={() => setShowChangeNameDialog(false)}
                    featureName={heading}
                    token={user.token}
                />
            )}
        </>
    );

    const PageBodyContainer = ({ children, isFetchingDigitalMemberCard }) => {
        return (
            <PortalPageShell>
                <PageHeader />

                <DigitalWorkspaceSurface $isCustomise={renderPage !== DIGITAL_MEMBER_CARD_PAGES.DISPLAY_COMPANY_CARD}>
                    { isFetchingDigitalMemberCard && <LottieLoadingIndicator /> }
                    { !isFetchingDigitalMemberCard && children }
                </DigitalWorkspaceSurface>
            </PortalPageShell>
        );
    };

    return (
        <PageBodyContainer isFetchingDigitalMemberCard={isFetchingDigitalMemberCard}>
            <DigitalCompanyCardContext.Provider value={{ 
                user: user,
                renderDisplayCards: () => setRenderPage(DIGITAL_MEMBER_CARD_PAGES.DISPLAY_COMPANY_CARD),
                renderCustomiseEmployee: () => setRenderPage(DIGITAL_MEMBER_CARD_PAGES.CUSTOMISE_EMPLOYEE_CARD),
                renderCustomiseClient: () => setRenderPage(DIGITAL_MEMBER_CARD_PAGES.CUSTOMISE_CLIENT_CARD),
                getCardTemplateByUserType: getCardTemplateByUserType,
                employeeCardInfo: employeeCardInfo,
                clientCardInfo: clientCardInfo,
                digitalMembershipCards: digitalMembershipCards,
                fetchDigitalMemberCard: fetchDigitalMemberCard,
                useGetHeroImages: {
                    businessHeroImages, 
                    setBusinessHeroImages,
                    meHeroImages,
                    isFetchingHeroImages,
                    fetchHeroImages
                }
            }}>
                { renderPage === DIGITAL_MEMBER_CARD_PAGES.DISPLAY_COMPANY_CARD && <DisplayCompanyCards /> }
                { renderPage === DIGITAL_MEMBER_CARD_PAGES.CUSTOMISE_EMPLOYEE_CARD && <CustomiseUserTypeCard isClient={false} /> }
                { renderPage === DIGITAL_MEMBER_CARD_PAGES.CUSTOMISE_CLIENT_CARD && <CustomiseUserTypeCard isClient={true} /> }
            </DigitalCompanyCardContext.Provider>
        </PageBodyContainer>
    );
};

export default withRouter(DigitalMemberCard);
